import { getGretaDataToken, readStoredSession } from '@questlabs/auth-js';

// NCB calls flow through the backend proxy — secret key never ships to the browser.
// Disabling NCB on this project (server-side flag) cuts off all calls instantly.
const NCB_CONFIG = {
  conversationId: 'chat-801c8b86-bba0-4179-ad75-a0e6138267d6',
  proxyBase: 'https://rmvzorxcl35mttidiexhtp5g2m0hpsqo.lambda-url.us-east-2.on.aws',
  instance: '53179_memorial_sanctuary',
};

// Every NCB proxy request MUST carry ?Instance=&lt;instance&gt;.
function buildProxyUrl(path) {
  const url = new URL(path, NCB_CONFIG.proxyBase);
  url.searchParams.set('Instance', NCB_CONFIG.instance);
  return url.toString();
}

// All helpers return a uniform envelope: { status, data, ... }.
// On network or non-2xx errors returns { status: 'error', data: null, error, httpStatus } — never throws.
async function ncbRequest(url, options = {}) {
  try {
    const res = await fetch(url, {
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', ...options.headers },
      ...options,
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
      return { status: 'error', data: null, error: body?.message || body?.error || `HTTP ${res.status}`, httpStatus: res.status };
    }
    if (body && typeof body === 'object' && !Array.isArray(body) && typeof body.status === 'string' && 'data' in body) {
      return body;
    }
    return { status: 'success', data: body };
  } catch (err) {
    return { status: 'error', data: null, error: err?.message || 'Network error' };
  }
}

export async function authFetch(path, options = {}) {
  return ncbRequest(buildProxyUrl(`/auth/${path}`), options);
}

export async function dataFetch(path, options = {}) {
  return ncbRequest(buildProxyUrl(`/data/${path}`), options);
}

export async function publicDataFetch(path, options = {}) {
  return ncbRequest(buildProxyUrl(`/public-data/${path}`), options);
}

/**
 * Greta Auth-owned records. Greta's server verifies the session token and
 * assigns the row owner; callers must never send or filter by owner_id.
 */
const GRETA_DATA_BASE = 'https://addons.questera.ai/api/greta/ncb/chat-801c8b86-bba0-4179-ad75-a0e6138267d6';

export async function ncbData(op, table, payload = {}) {
  const session = readStoredSession()?.greta?.session ?? null;
  const token = await getGretaDataToken(session);
  if (!token) return { status: 'error', data: null, error: 'Not signed in' };

  try {
    const res = await fetch(`${GRETA_DATA_BASE}/user/${op}/${table}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });
    const body = await res.json().catch(() => null);
    if (!res.ok) {
      return {
        status: 'error',
        data: null,
        error: body?.error || body?.message || 'Request failed',
        httpStatus: res.status,
      };
    }
    return {
      status: 'success',
      data: body?.data ?? body,
      ...(body?.metadata ? { metadata: body.metadata } : {}),
    };
  } catch (err) {
    return { status: 'error', data: null, error: err?.message || 'Network error' };
  }
}

/**
 * Shared catalog data, not tied to a visitor's identity.
 */
export async function sharedData(table, params = {}) {
  const qs = new URLSearchParams(params).toString();
  try {
    const res = await fetch(`${GRETA_DATA_BASE}/shared/read/${table}${qs ? `?${qs}` : ''}`);
    const body = await res.json().catch(() => null);
    if (!res.ok) {
      return {
        status: 'error',
        data: null,
        error: body?.error || body?.message || 'Request failed',
        httpStatus: res.status,
      };
    }
    return { status: 'success', data: body?.data ?? body };
  } catch (err) {
    return { status: 'error', data: null, error: err?.message || 'Network error' };
  }
}

/**
 * BACKWARD COMPATIBILITY
 * Restores writeFetch for older components. Routes through the proxy but returns
 * the raw fetch Response so that existing `await res.json()` calls do not break.
 */
export async function writeFetch(path, options = {}) {
  const url = buildProxyUrl(`/data/${path}`);
  return fetch(url, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...options.headers },
    ...options,
  });
}

/**
 * Retrieves available authentication providers
 */
export async function getAuthProviders() {
  try {
    const res = await authFetch('providers');
    return res?.data || { email: true, google: false };
  } catch (error) {
    console.error('[api] getAuthProviders error:', error);
    return { email: true, google: false };
  }
}

/**
 * Lightweight connectivity check.
 * Returns { connected: boolean, httpStatus: number|null, error: string|null }.
 * A network-level failure means the proxy is unreachable.
 * Any HTTP response (even 4xx/5xx) means the proxy is online.
 */
export async function checkNCBStatus() {
  try {
    const url = buildProxyUrl('/public-data/read/health_probe');
    const res = await fetch(url, {
      method: 'GET',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    });
    // Proxy responded — we are connected, even if the table doesn't exist
    return { connected: true, httpStatus: res.status, error: null };
  } catch (err) {
    return { connected: false, httpStatus: null, error: err?.message || 'Network error. The backend proxy may be unreachable.' };
  }
}

/**
 * SESSION MANAGEMENT
 */
export function getSession() {
  try {
    const stored = localStorage.getItem('ncb_user');
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
}

export function setSession(user) {
  if (user) localStorage.setItem('ncb_user', JSON.stringify(user));
}

export function clearSession() {
  localStorage.removeItem('ncb_token');
  localStorage.removeItem('ncb_user');
}